/**
 * 주소 검색 — chat-server `POST /api/v1/address/search` (2026-09-17).
 *
 * ★ 검색어는 «본문»으로 보낸다. 요청 로그가 URL 을 쿼리째 남기므로 GET 쿼리로 보내지 않는다(서버도 GET 을 안 받는다).
 * ★ 이 파일은 요청 한 번만 한다 — 언제 부를지(적는 동안에는 안 부른다)는 찾기 창이 정한다.
 */
import { apiClient } from './client';

/** 도로명주소 API 한 줄 — 서버가 필요한 칸만 골라 준다(없는 칸은 빈 글자). */
export interface JusoAddress {
  /** 도로명주소 전체(참고항목 괄호 포함) — 「주소」 칸에 넣는 값. */
  roadAddr: string;
  roadAddrPart1: string;
  roadAddrPart2: string;
  jibunAddr: string;
  zipNo: string;
  siNm: string;
  sggNm: string;
  emdNm: string;
  hemdNm: string;
  liNm: string;
  bdNm: string;
  admCd: string;
  rnMgtSn: string;
  bdMgtSn: string;
}

export interface AddressSearchResult {
  totalCount: number;
  page: number;
  perPage: number;
  items: JusoAddress[];
}

export async function searchAddress(input: { keyword: string; page?: number; perPage?: number }): Promise<AddressSearchResult> {
  const { data } = await apiClient.post<AddressSearchResult>('/address/search', input);
  return data;
}

/**
 * 실패를 찾기 창이 그릴 갈래로 가른다.
 *   · `keyword`     400 — 검색어가 공급자 기준에 안 맞는다(부르지도 않았다)
 *   · `too_broad`   422 — 결과가 너무 많다
 *   · `unavailable` 503 — 연결 안 됨(공급자 · 키 · 회사가 끔)
 *   · `failed`      그 밖 — 502(키 거절 · 한도 · 닿지 못함) · 네트워크 · 429 등
 */
export type AddressSearchFailure =
  | { kind: 'keyword'; code: string; message: string | null }
  | { kind: 'too_broad' }
  | { kind: 'unavailable'; reason: string | null }
  | { kind: 'failed'; status: number | null };

export function classifyAddressSearchError(err: unknown): AddressSearchFailure {
  const res = (err as { response?: { status?: number; data?: { error?: unknown; message?: unknown; reason?: unknown } } } | null)?.response;
  const status = typeof res?.status === 'number' ? res.status : null;
  const data = res?.data ?? {};
  const str = (v: unknown) => (typeof v === 'string' ? v : null);
  if (status === 400) return { kind: 'keyword', code: str(data.error) ?? '', message: str(data.message) };
  if (status === 422 && data.error === 'TOO_BROAD') return { kind: 'too_broad' };
  // 503 이라도 주소 라우트의 답일 때만 «연결 안 됨» — 인증 기반 503(AUTH_INFRA_UNAVAILABLE) 등은 «실패»다.
  if (status === 503 && data.error === 'ADDRESS_SEARCH_UNAVAILABLE') return { kind: 'unavailable', reason: str(data.reason) };
  return { kind: 'failed', status };
}
